<li
    {id}
    role="treeitem"
    on:click={toggleSelection}
    on:keydown={keyHandler}
    tabindex={selected ? 0 : -1}
    class="prom-resource-tree-item"
    aria-expanded={isFolder ? expanded : undefined}
    aria-busy={resource.childrenLoading && !children.length}
    aria-selected={resource.checked}
    data-resource-id={resource.id}
>
    <div
        class="prom-resource-tree-item__title"
        class:prom-resource-tree-item__title--inactive={!resource.active}
        class:prom-resource-tree-item__title--expanded={expanded}
    >
        <div class="prom-checkbox">
            <input
                class="prom-checkbox__input"
                type="checkbox"
                bind:checked={resource.checked}
                disabled={resource.disabled}
                aria-labelledby="{id}-title"
                tabindex={-1}
                id="{id}-checkbox"
            />

            <span class="prom-checkbox__custom-check"></span>
        </div>

        {#if isFolder}
            <button
                class="prom-resource-tree-item__expand-button"
                class:prom-resource-tree-item__expand-button--is-loading={resource.childrenLoading && !children.length}
                class:prom-resource-tree-item__expand-button--expanded={expanded}
                aria-hidden="true"
                on:click={toggleExpanded}
                tabindex={-1}
            >
                <span class="screen-reader">{i18n.expand}</span>
            </button>
        {:else}
            <div class="prom-resource-tree-item__empty-space"></div>
        {/if}
        <img width={24} height={24} src={resource.iconUrl} alt="" />

        <span id={id + '-title'}>{resource.title}</span>
    </div>
    {#if expanded && !resource.childrenLoading && resource.totalChildren === 0}
        <p class="prom-resource-tree-item__empty">{i18n.folderEmptyState}</p>
    {:else if expanded}
        <ul role="group" class="prom-resource-tree-item__children" in:slide>
            {#each children as r (r.id)}
                <svelte:self {i18n} resource={r} bind:selectedId {resources} on:loadChildren />
            {/each}
            {#if resource.childrenLoading || resource.childrenLoaded < resource.totalChildren}
                <ScrollTrigger on:visible={() => dispatch('loadChildren', resource)} />
            {/if}
        </ul>
    {/if}
</li>

<script>
    import ScrollTrigger from './ScrollTrigger.svelte';
    import { Keys } from '@itslearning/atlas/keyboard/keys';
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';
    import { slide } from 'svelte/transition';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    /**
     * @typedef Resource
     * @type {object}
     * @property {number} id
     * @property {boolean} checked
     * @property {boolean} active
     * @property {number} parentId
     * @property {string} title
     * @property {string} type
     * @property {string} iconUrl
     * @property {boolean} [disabled]
     * @property {boolean} [childrenLoading]
     * @property {number} [childrenLoaded]
     * @property {number} [totalChildren]
     * @property {boolean} [expanded]
     */

    /** @type { Resource }*/
    export let resource;
    /** @type { Resource[] }*/
    export let resources = [];
    /** @type { Number }*/
    export let selectedId = 0;

    /** @type {{ [folderEmptyState: string] }} i18n terms for the component */
    export let i18n = { folderEmptyState: '', expand: 'Expand' };

    let expanded = false;

    $: id = `resource-tree-item-${resource.id}`;
    $: children = resources.filter(r => r.parentId === resource.id);
    $: selected = selectedId === resource.id;
    $: isFolder = resource.type === 'Folder';

    function toggleExpanded(e) {
        e.preventDefault();
        e.stopPropagation();

        expanded = !expanded;

        dispatch('loadChildren', resource);
    }

    function toggleSelection(e) {
        e.preventDefault();
        e.stopPropagation();

        if (!resource.disabled) {
            resource.checked = !resource.checked;
        }
    }

    function keyHandler(event) {
        const { key } = event;

        switch (normalizeKey(key)) {
            case Keys.SPACE:
                toggleSelection(event);
                break;
            case Keys.ENTER:
                if (isFolder) {
                    toggleExpanded(event);
                } else {
                    toggleSelection(event);
                }
                break;
            case Keys.RIGHT:
                onRight(event);
                break;
            case Keys.LEFT:
                onLeft(event);
                break;
            case Keys.DOWN:
                selectRelative(event, 1);
                break;
            case Keys.UP:
                selectRelative(event, -1);
                break;
        }
    }

    function onLeft(event) {
        if (expanded && isFolder) {
            toggleExpanded(event);
        } else {
            const items = [...document.querySelector('.prom-resource-tree').querySelectorAll('.prom-resource-tree-item')];
            const parent = items.find(i => i.id === `resource-tree-item-${resource.parentId}`);

            if (parent) {
                // @ts-ignore .focus for the element
                parent.focus();
            }
        }
    }

    function onRight(event) {
        if (expanded) {
            selectRelative(event, 1);
        } else if (isFolder) {
            toggleExpanded(event);
        }
    }

    function selectRelative(event, offset) {
        event.preventDefault();
        event.stopPropagation();

        const items = [...document.querySelector('.prom-resource-tree').querySelectorAll('.prom-resource-tree-item')];
        const itemIndex = items.findIndex(i => i.id === id);

        if (itemIndex + offset < items.length && itemIndex + offset >= 0) {
            // @ts-ignore .dataset for the element
            selectedId = Number.parseInt(items[itemIndex + offset].dataset["resourceId"]);
            // @ts-ignore .focus for the element
            items[itemIndex + offset].focus();
        }
    }
</script>
